import React, { useEffect, useRef } from 'react';
import './FloatingLogos.css';

interface LogoConfig {
  name: string;
  icon: string;
}

const TECH_LOGOS: LogoConfig[] = [
  { name: 'Vue', icon: '/assets/logos/vue.svg' },
  { name: 'Angular', icon: '/assets/logos/angular.svg' },
  { name: 'React', icon: '/assets/logos/react.svg' },
  { name: 'Node', icon: '/assets/logos/node.svg' },
  { name: 'Java', icon: '/assets/logos/java.svg' },
  { name: 'Spring', icon: '/assets/logos/spring.svg' },
  { name: 'GraphQL', icon: '/assets/logos/graphql.svg' },
  { name: 'Kubernetes', icon: '/assets/logos/k8.svg' },
  { name: 'Docker', icon: '/assets/logos/docker.svg' },
  { name: 'AWS', icon: '/assets/logos/aws.svg' },
  { name: 'TypeScript', icon: '/assets/logos/typescript.svg' },
  { name: 'Python', icon: '/assets/logos/py.svg' },
  { name: 'Vite', icon: '/assets/logos/Vite.js.svg' },
  { name: 'Three.js', icon: '/assets/logos/threejs.svg' },
  { name: 'Jenkins', icon: '/assets/logos/jenkins.svg' },
  { name: 'Bash', icon: '/assets/logos/bash.svg' },
  { name: 'Fastify', icon: '/assets/logos/fastify.svg' },
  { name: 'Storybook', icon: '/assets/logos/storybook.svg' },
  { name: 'Jest', icon: '/assets/logos/jest.svg' },
  { name: 'Webpack', icon: '/assets/logos/webpack.svg' },
  { name: 'C++', icon: '/assets/logos/cplusplus.svg' },
  { name: 'Cloudflare Workers', icon: '/assets/logos/cloudflare.svg' },
  { name: 'Argo', icon: '/assets/logos/argo.svg' },
  { name: 'C', icon: '/assets/logos/c.svg' },
  { name: 'C#', icon: '/assets/logos/csharp.svg' },
  { name: 'CSS', icon: '/assets/logos/css.svg' },
  { name: 'Debian', icon: '/assets/logos/debian.svg' },
  { name: 'ESLint', icon: '/assets/logos/eslint.svg' },
  { name: 'GitHub', icon: '/assets/logos/github.svg' },
  { name: 'GitLab', icon: '/assets/logos/gitlab.svg' },
  { name: 'HTML', icon: '/assets/logos/html.svg' },
  { name: 'JavaScript', icon: '/assets/logos/javascript.svg' },
  { name: 'Linux', icon: '/assets/logos/linux.svg' },
  { name: 'Material', icon: '/assets/logos/material.svg' },
  { name: 'MongoDB', icon: '/assets/logos/mongo.svg' },
  { name: 'MySQL', icon: '/assets/logos/mysql.svg' },
  { name: 'Next.js', icon: '/assets/logos/nextjs.svg' },
  { name: 'Nginx', icon: '/assets/logos/nginx.svg' },
  { name: 'Nuxt.js', icon: '/assets/logos/nuxtjs.svg' },
  { name: 'Playwright', icon: '/assets/logos/playwrite.svg' },
  { name: 'Redis', icon: '/assets/logos/redis.svg' },
  { name: 'Sass', icon: '/assets/logos/sass.svg' },
  { name: 'Swagger', icon: '/assets/logos/swagger.svg' },
  { name: 'Unity', icon: '/assets/logos/unity.svg' },
  { name: 'Unreal', icon: '/assets/logos/unreal.svg' },
  { name: 'VS Code', icon: '/assets/logos/vscode.svg' },
];

const LOGO_SIZE = 64;
const MIN_SPEED = 0.3;
const MAX_SPEED = 1.8;
const CARD_PADDING = 10;
const EDGE_OVERFLOW = 32;
const IMPULSE_RADIUS = 150;
const IMPULSE_STRENGTH = 5;

interface LogoPhysics {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export const FloatingLogos: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const elemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const physicsRef = useRef<LogoPhysics[]>([]);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    let mounted = true;

    const vw = window.innerWidth;
    const cardEl2 = document.querySelector('.entry-card');
    const cardBottom = cardEl2 ? cardEl2.getBoundingClientRect().bottom + 80 : window.innerHeight;
    const vh = Math.max(window.innerHeight, cardBottom);
    const cardEl = document.querySelector('.entry-card');
    const cr = cardEl?.getBoundingClientRect();

    // Initialize each logo with random position (outside card) and random velocity
    physicsRef.current = TECH_LOGOS.map(() => {
      const speed = MIN_SPEED + Math.random() * (MAX_SPEED - MIN_SPEED);
      const angle = Math.random() * Math.PI * 2;
      let x: number, y: number;
      let tries = 0;

      do {
        x = Math.random() * (vw - LOGO_SIZE);
        y = Math.random() * (vh - LOGO_SIZE);
        tries++;
      } while (
        tries < 50 &&
        cr &&
        x + LOGO_SIZE > cr.left - CARD_PADDING &&
        x < cr.right + CARD_PADDING &&
        y + LOGO_SIZE > cr.top - CARD_PADDING &&
        y < cr.bottom + CARD_PADDING
      );

      return {
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
      };
    });

    // Apply initial positions immediately so logos don't flash at (0,0)
    physicsRef.current.forEach((logo: LogoPhysics, i: number) => {
      const el = elemsRef.current[i];
      if (el) {
        el.style.transform = `translate(${logo.x}px, ${logo.y}px)`;
      }
    });

    const animate = () => {
      if (!mounted) return;
      const logos = physicsRef.current;
      const w = window.innerWidth;
      const scrollOverlay = document.querySelector('.entry-overlay') as HTMLElement | null;
      const cardRect = document.querySelector('.entry-card')?.getBoundingClientRect();
      const scrollTop = scrollOverlay ? scrollOverlay.scrollTop : 0;
      // Use the card's bottom edge + margin as the boundary to avoid feedback loop
      const cardBottom = cardRect ? cardRect.bottom + scrollTop + 80 : window.innerHeight;
      const h = Math.max(window.innerHeight, cardBottom);
      const r = LOGO_SIZE / 2;

      // Update positions
      for (const l of logos) {
        l.x += l.vx;
        l.y += l.vy;
      }

      // Screen edge collisions (allow slight overflow before bouncing)
      for (const l of logos) {
        if (l.x < -EDGE_OVERFLOW) {
          l.x = -EDGE_OVERFLOW;
          l.vx = Math.abs(l.vx);
        }
        if (l.x + LOGO_SIZE > w + EDGE_OVERFLOW) {
          l.x = w + EDGE_OVERFLOW - LOGO_SIZE;
          l.vx = -Math.abs(l.vx);
        }
        if (l.y < -EDGE_OVERFLOW) {
          l.y = -EDGE_OVERFLOW;
          l.vy = Math.abs(l.vy);
        }
        if (l.y + LOGO_SIZE > h) {
          l.y = h - LOGO_SIZE;
          l.vy = -Math.abs(l.vy);
        }

        // Gentle friction to bring boosted logos back to normal speed
        const spd = Math.sqrt(l.vx * l.vx + l.vy * l.vy);
        if (spd > MAX_SPEED) {
          l.vx *= 0.995;
          l.vy *= 0.995;
        }
      }

      // Card collision (circle vs AABB) — offset card rect by scroll position
      if (cardRect) {
        const pad = {
          left: cardRect.left - CARD_PADDING,
          top: Math.max(0, cardRect.top + scrollTop - CARD_PADDING),
          right: cardRect.right + CARD_PADDING,
          bottom: Math.min(h, cardRect.bottom + scrollTop + CARD_PADDING),
        };

        for (const l of logos) {
          const cx = l.x + r;
          const cy = l.y + r;

          const nearestX = Math.max(pad.left, Math.min(cx, pad.right));
          const nearestY = Math.max(pad.top, Math.min(cy, pad.bottom));

          const dx = cx - nearestX;
          const dy = cy - nearestY;
          const distSq = dx * dx + dy * dy;

          if (distSq < r * r) {
            if (distSq > 0) {
              const dist = Math.sqrt(distSq);
              const overlap = r - dist;
              const ux = dx / dist;
              const uy = dy / dist;

              l.x += ux * overlap;
              l.y += uy * overlap;

              const dot = l.vx * ux + l.vy * uy;
              l.vx -= 2 * dot * ux;
              l.vy -= 2 * dot * uy;
            } else {
              // Center is inside card — push to nearest edge
              const dL = cx - pad.left;
              const dR = pad.right - cx;
              const dT = cy - pad.top;
              const dB = pad.bottom - cy;
              const min = Math.min(dL, dR, dT, dB);

              if (min === dL) {
                l.x = pad.left - LOGO_SIZE;
                l.vx = -Math.abs(l.vx);
              } else if (min === dR) {
                l.x = pad.right;
                l.vx = Math.abs(l.vx);
              } else if (min === dT) {
                l.y = pad.top - LOGO_SIZE;
                l.vy = -Math.abs(l.vy);
              } else {
                l.y = pad.bottom;
                l.vy = Math.abs(l.vy);
              }
            }
          }
        }
      }

      // Logo-logo collisions (elastic, equal mass)
      for (let i = 0; i < logos.length; i++) {
        for (let j = i + 1; j < logos.length; j++) {
          const a = logos[i];
          const b = logos[j];

          const dx = b.x + r - (a.x + r);
          const dy = b.y + r - (a.y + r);
          const distSq = dx * dx + dy * dy;
          const minDist = LOGO_SIZE;

          if (distSq < minDist * minDist && distSq > 0) {
            const dist = Math.sqrt(distSq);
            const overlap = minDist - dist;
            const ux = dx / dist;
            const uy = dy / dist;

            // Push apart
            a.x -= (ux * overlap) / 2;
            a.y -= (uy * overlap) / 2;
            b.x += (ux * overlap) / 2;
            b.y += (uy * overlap) / 2;

            // Elastic velocity exchange along collision normal
            const relVx = a.vx - b.vx;
            const relVy = a.vy - b.vy;
            const dot = relVx * ux + relVy * uy;

            if (dot > 0) {
              a.vx -= dot * ux;
              a.vy -= dot * uy;
              b.vx += dot * ux;
              b.vy += dot * uy;
            }
          }
        }
      }

      // Update DOM directly (no React re-renders)
      for (let i = 0; i < logos.length; i++) {
        const el = elemsRef.current[i];
        if (el) {
          el.style.transform = `translate(${logos[i].x}px, ${logos[i].y}px)`;
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    const handleClick = (e: MouseEvent) => {
      const card = document.querySelector('.entry-card');
      if (card && card.contains(e.target as Node)) return;

      const overlayEl = document.querySelector('.entry-overlay') as HTMLElement | null;
      const clickX = e.clientX;
      const clickY = e.clientY + (overlayEl ? overlayEl.scrollTop : 0);

      // Push nearby logos away
      for (const logo of physicsRef.current) {
        const cx = logo.x + LOGO_SIZE / 2;
        const cy = logo.y + LOGO_SIZE / 2;
        const dx = cx - clickX;
        const dy = cy - clickY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < IMPULSE_RADIUS && dist > 0) {
          const force = (1 - dist / IMPULSE_RADIUS) * IMPULSE_STRENGTH;
          logo.vx += (dx / dist) * force;
          logo.vy += (dy / dist) * force;
        }
      }

      // Pop visual at click point
      if (containerRef.current) {
        const pop = document.createElement('div');
        pop.className = 'click-pop';
        pop.style.left = `${clickX}px`;
        pop.style.top = `${clickY}px`;
        containerRef.current.appendChild(pop);
        pop.addEventListener('animationend', () => pop.remove());
      }
    };

    window.addEventListener('click', handleClick);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      mounted = false;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <div className="floating-logos-container" ref={containerRef}>
      {TECH_LOGOS.map((logo, i) => (
        <div
          key={logo.name}
          ref={(el) => {
            elemsRef.current[i] = el;
          }}
          className="floating-logo"
        >
          <img src={logo.icon} alt={logo.name} className="floating-logo-img" />
        </div>
      ))}
    </div>
  );
};
